/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ComponentPropsWithoutRef, ReactElement } from 'react';
import { NavLink } from '@/components/link';
import type { NavLinkItem } from '@/lib/header-menu/types';

type MenuHrefProps = { link: NavLinkItem } & Omit<ComponentPropsWithoutRef<'a'>, 'href'>;

/**
 * Header menu anchor. Internal hrefs go through the site-aware `NavLink` (adds the site/locale
 * prefix); external `https://` hrefs render a plain anchor. Forwards all props so it can sit
 * under Radix `asChild` slots.
 */
export default function MenuHref({ link, children, ...props }: MenuHrefProps): ReactElement {
    if (link.external) {
        return (
            <a {...props} href={link.href} rel="noopener">
                {children}
            </a>
        );
    }
    return (
        <NavLink {...props} to={link.href}>
            {children}
        </NavLink>
    );
}
